import React, { Component } from 'react';
import { connect } from 'react-redux';
import { heatSelect } from '~/store/actions/heat';
import { isLiveChanged } from '~/store/actions/is-live';
import PropTypes from 'prop-types';
import { popup, translate } from '~/decorators';
import Scrollable from '~/views/Home/base/Scrollable';
import SizeInformer from '~/views/Home/base/SizeInformer';
import TabSwitcher from '~/views/Home/base/TabSwitcher';
import oget from 'oget';
import isEqual from 'lodash.isequal';
import LeaderboardGroup from './Group';

import './index.scss';
import isLeaderboardWithDetail from '~/utils/isLeaderboardWithDetail';
import isLeaderboardWithTotal from '~/utils/isLeaderboardWithTotal';

export default
@popup
@translate('home.table.leaderboard')
@connect(null, (dispatch) => ({
  onHeatSelect: (...args) => dispatch(heatSelect(...args)),
  onIsLiveChanged: (...args) => dispatch(isLiveChanged(...args)),
}))
class TableLeaderboard extends Component {
  state = {
    activeExternalAthleteId: null,
    scrollToTop: false,
    scrollToLive: false,
    hasScrollbar: false,
    hasLiveAthleteRank: false,
    isLiveAthleteTop10: false,
    roundConfig: {},
    leaderboard: [],
    tabsHeight: 0,
    fullScreenMode: false,
  };

  static propTypes = {
    round: PropTypes.oneOfType([(item) => item === null, PropTypes.object.isRequired]),
    contest: PropTypes.oneOfType([(item) => item === null, PropTypes.object.isRequired]),
    event: PropTypes.shape({
      startListColumns: PropTypes.object,
      leaderboardColumns: PropTypes.object,
      leaderboardDetails: PropTypes.object,
      showHeadshotsInLeaderboard: PropTypes.bool,
    }).isRequired,
    heats: PropTypes.array.isRequired,
    heatsById: PropTypes.object.isRequired,
    heatId: PropTypes.any,
    roundState: PropTypes.any,
    athletes: PropTypes.shape({
      expectedHeatId: PropTypes.any,
      byId: PropTypes.object,
      isLiveId: PropTypes.any,
    }).isRequired,
    isLive: PropTypes.shape({
      athleteId: PropTypes.any,
      heatId: PropTypes.any,
      changed: PropTypes.bool,
    }).isRequired,
    colors: PropTypes.shape({
      backgroundColor: PropTypes.string,
      color: PropTypes.string,
      itemOdd: PropTypes.shape({
        backgroundColor: PropTypes.string,
        borderColor: PropTypes.string,
      }),
      itemEven: PropTypes.shape({
        backgroundColor: PropTypes.string,
        borderColor: PropTypes.string,
      }),
      itemActive: PropTypes.shape({
        background: PropTypes.string,
        borderColor: PropTypes.string,
        color: PropTypes.string,
      }),
      itemNext: PropTypes.shape({
        borderColor: PropTypes.string,
      }),
      scores: PropTypes.shape({
        default: PropTypes.shape({
          color: PropTypes.string,
        }),
        active: PropTypes.shape({
          color: PropTypes.string,
        }),
        highlight: PropTypes.shape({
          color: PropTypes.string,
        }),
        highlightActive: PropTypes.shape({
          color: PropTypes.string,
        }),
        extra: PropTypes.shape({
          color: PropTypes.string,
        }),
        extraActive: PropTypes.shape({
          color: PropTypes.string,
        }),
        drop: PropTypes.shape({
          color: PropTypes.string,
        }),
      }),
    }).isRequired,
    deviceType: PropTypes.string.isRequired,
  };

  static getDerivedStateFromProps(
    { isLive, heatsById, heatId, round, event },
    { activeExternalAthleteId: prevActiveExternalAthleteId, leaderboard: prevLeaderboard },
  ) {
    const leaderboard = oget(heatsById, `[${heatId}].results`, []);

    const { hasTricks, labelsCount, ...info } = oget(heatsById, `[${heatId}].info`, {});

    const roundConfig = {
      cut: oget(round, 'cut'),
      showTotal: isLeaderboardWithTotal(leaderboard),
      showNeeds: oget(round, 'showneeds', oget(round, 'showNeeds')) !== false,
      showRuns: isLeaderboardWithDetail(leaderboard, 'Runs'),
      autoFocus: oget(round, 'autofocus', oget(round, 'autoFocus')) !== false,
      labelsCount,
      mostRunsCount: info.mostRunsCount,
      mostTricksCount: info.mostTricksCount,
      mostJudgesCount: 5,
      showJudgeScores: event.hasOwnProperty('leaderboardDetails') ? !!event.leaderboardDetails['Judges Scores'] : false,
      testData: oget(heatsById, `[${heatId}].testData`, ''),
    };

    const activeExternalAthleteId = isLive.athleteId;

    const liveAthlete = leaderboard.reduce((athlete, group) => {
      if (athlete) {
        return true;
      }
      return group.find(({ externalAthleteId }) => externalAthleteId === activeExternalAthleteId);
    }, null);

    const hasLiveAthleteRank = !!liveAthlete && liveAthlete.rank;
    const isLiveAthleteTop10 = hasLiveAthleteRank && liveAthlete.rank <= 10;

    if (!isEqual(prevLeaderboard, leaderboard)) {
      return {
        leaderboard,
        roundConfig,
        scrollToTop: true,
        scrollToLive: true,
        hasLiveAthleteRank,
        isLiveAthleteTop10,
      };
    }

    if (!activeExternalAthleteId) {
      return {
        leaderboard,
        roundConfig,
      };
    }

    if (activeExternalAthleteId === prevActiveExternalAthleteId) {
      return {
        leaderboard,
        roundConfig,
      };
    }

    return {
      leaderboard,
      roundConfig,
      activeExternalAthleteId,
      scrollToLive: true,
      hasLiveAthleteRank,
      isLiveAthleteTop10,
    };
  }

  removeContentRefs = () => {
    this.scrollDiv = null;
    this.isLiveDiv = null;
  };

  componentWillUnmount() {
    this.removeContentRefs();
    this.containerDiv = null;
  }

  componentDidUpdate() {
    const {
      scrollToLive,
      scrollToTop,
      hasLiveAthleteRank,
      isLiveAthleteTop10,
      roundConfig: { autoFocus },
    } = this.state;

    if (!this.scrollDiv) {
      return;
    }

    if (!autoFocus && scrollToTop) {
      this.scrollDiv.scrollTop = 0;

      this.setState({
        scrollToTop: false,
      });
    }

    if (autoFocus && scrollToLive) {
      const shouldScrollToTop = this.isLiveDiv && hasLiveAthleteRank && !isLiveAthleteTop10;
      let offsetTop = 0; // scroll to top by default
      if (shouldScrollToTop) {
        // scroll to `isLiveDiv` offsetTop and add a padding of 3 times its clientHeight from the top, and make sure that if it's negative then add clientHeight until it's no longer negative
        offsetTop = this.isLiveDiv.offsetTop - 3 * this.isLiveDiv.clientHeight;
      }

      this.scrollDiv.scrollTop = offsetTop;

      this.setState({
        scrollToLive: false,
      });
    }

    const { isLive, heatsById, onIsLiveChanged, onHeatSelect } = this.props;

    if (!isLive.changed && isLive.heatId) {
      onIsLiveChanged();

      onHeatSelect(heatsById[isLive.heatId]);
    }
  }

  lastResizeDate = new Date();
  lastWidth = 0;

  listWidth = () => {
    if (!this.containerDiv) {
      return 0;
    }

    const style = window.getComputedStyle(this.containerDiv.parentNode);

    const width = parseInt(style.width, 10);

    if (width !== this.lastWidth) {
      this.lastWidth = width;
      this.lastResizeDate = new Date();
    }

    return width;
  };

  className = () => {
    if (!this.containerDiv) {
      return '';
    }

    return `width-${this.listWidth()}`;
  };

  onScrollableUpdate = ({ hasScrollbar }) =>
    this.setState({
      hasScrollbar,
    });

  toggleFullscreenMode = () => {
    this.props.toggleFullscreenMode();
  };

  // eslint-disable-next-line
  render() {
    const {
      t,
      athletes,
      heats,
      heatId,
      onHeatSelect,
      colors,
      event: { leaderboardColumns, showHeadshotsInLeaderboard, id: eventId },
      round,
      roundActive,
      deviceType,
      popupHide,
      fullscreenMode,
      fullscreenModeForiFrame,
    } = this.props;

    const { leaderboard, roundConfig, hasScrollbar, tabsHeight } = this.state;

    const roundType = oget(round, 'type');
    const fullscreenIcon = {
      color: colors.itemActive.backgroundColor,
    };
    const hasTabs = heats.length > 1;
    const showGroupTitle = leaderboard.length > 1 || roundType === 'Time';
    const fullscreenClass = fullscreenMode
      ? 'icon-size-actual icons fullscreen-exit'
      : 'icon-size-fullscreen icons fullscreen';
    return (
      <div
        ref={(ref) => {
          this.containerDiv = ref;

          if (!leaderboard.length) {
            this.removeContentRefs();
          }
        }}
        className={[
          'table-leaderboard',
          hasScrollbar ? 'has-scrollbar' : '',
          hasTabs ? 'has-tabs' : '',
          this.className(),
        ].join(' ')}
      >
        {!fullscreenModeForiFrame && (
          <i className={fullscreenClass} style={fullscreenIcon} onClick={this.toggleFullscreenMode} />
        )}
        <div className="table-title">
          {t('Leaderboard')}
          {round && round.info ? <span className="round-info">{round.info}</span> : null}
        </div>

        {hasTabs && (
          <SizeInformer
            lastUpdate={this.lastResizeDate}
            onResized={({ height }) => {
              this.setState({
                tabsHeight: height,
              });
            }}
          >
            <TabSwitcher
              tabs={heats}
              renderTab={(heat, i) => {
                let classActive = '';
                let style = {};
                if (heat.externalId === heatId) {
                  classActive = 'active';
                  style = {
                    backgroundColor: colors.itemActive.backgroundColor,
                  };
                }
                return (
                  <span
                    key={`startlist_heats_tabs_${i}`}
                    className={classActive}
                    style={style}
                    onClick={() => onHeatSelect(heat)}
                  >
                    {heat.name}
                  </span>
                );
              }}
            />
          </SizeInformer>
        )}

        {!leaderboard.length && <div className="no-info">{t('No live information at this time')}</div>}

        {leaderboard.length > 0 && (
          <div className="head">
            <div className="cell cell-no">{t('Rk')}</div>

            {showHeadshotsInLeaderboard && deviceType !== 'phone' && <div className="cell cell-headshot" />}

            {leaderboardColumns.Bib && <div className="cell cell-bib">{t('Bib')}</div>}

            <div className="cell cell-name">{leaderboardColumns.Nation ? t('Name/Nation') : t('Name')}</div>

            {(roundConfig.showTotal || roundConfig.showNeeds || roundConfig.showRuns) && (
              <div className="cell cell-scores-head">{roundType === 'Time' ? t('Time') : t('Scores')}</div>
            )}
          </div>
        )}

        {leaderboard.length > 0 && (
          <Scrollable
            onUpdate={this.onScrollableUpdate}
            onScroll={popupHide}
            onContentRef={(ref) => (this.scrollDiv = ref)}
            style={hasTabs ? { top: tabsHeight + 47 } : {}}
          >
            {leaderboard.map((group, i) => (
              <div key={`leaderboard_heat_category_${i}`} className="heat-category">
                {showGroupTitle && <div className="category-title">{oget(group, '[0].category', '')}</div>}

                <LeaderboardGroup
                  key={`leaderboard_group_${i}`}
                  i={i}
                  t={t}
                  group={group}
                  roundActive={roundActive}
                  colors={colors}
                  athletesById={athletes.byId}
                  columns={leaderboardColumns}
                  roundConfig={roundConfig}
                  roundType={roundType}
                  listWidth={this.listWidth()}
                  tableWidth={!this.containerDiv ? 0 : this.containerDiv.clientWidth}
                  deviceType={deviceType}
                  showHeadshotsInLeaderboard={showHeadshotsInLeaderboard}
                  eventId={eventId}
                  onRowRef={(ref, { athlete }, i) => {
                    if (athlete.isLive) {
                      this.isLiveDiv = ref;
                    }
                  }}
                />
              </div>
            ))}
          </Scrollable>
        )}
      </div>
    );
  }
}
